import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Select, Table, Tag, Tooltip } from 'antd';
import { AccessService } from '../../service/api/access-service';
import { buildCatalogTree, CRUD_CODES } from './access-utils';

/**
 * Union of grants for any combination of roles — e.g. a user holding WMS_User + ShipmentAdministrator.
 * Shows which role supplies each grant. Preview only; it does not impersonate anyone.
 */
const PreviewTab = () => {
  const [roles, setRoles] = useState([]);
  const [roleIds, setRoleIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AccessService.getRoles().then(setRoles).catch(() => {});
  }, []);

  useEffect(() => {
    if (roleIds.length === 0) {
      setPreview(null);
      return;
    }
    setLoading(true);
    AccessService.getPreview(roleIds)
      .then(setPreview)
      .catch(() => setPreview(null))
      .finally(() => setLoading(false));
  }, [roleIds]);

  const roleName = useMemo(() => Object.fromEntries(roles.map(r => [r.id, r.entraRoleValue])), [roles]);

  // Only active pages/modules/permissions take effect; VIEW is implied by any other grant on the page.
  const tree = useMemo(() => {
    if (!preview) return [];
    const sources = preview.sources || {};
    const effective = node => {
      const children = (node.children || []).map(effective).filter(Boolean);
      const item = node.type === 'module' ? node.module : node.page;
      if (!item.isActive) return null;
      if (node.type === 'page') {
        const granted = node.perms.filter(p => p.isActive && (preview.isSuperAdmin || sources[p.id]));
        const hasAny = granted.length > 0;
        return { ...node, granted, impliedView: hasAny && !granted.some(p => p.code === 'VIEW'), children: children.length ? children : undefined };
      }
      return { ...node, children: children.length ? children : undefined };
    };
    return buildCatalogTree(preview.catalog).map(effective).filter(Boolean);
  }, [preview]);

  const sourceTag = perm => {
    if (preview.isSuperAdmin) return <Tag color="red">super-admin</Tag>;
    const ids = preview.sources?.[perm.id] || [];
    return (
      <Tooltip title={`Granted by: ${ids.map(id => roleName[id]).join(', ')}`}>
        <Tag color="green">✓</Tag>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'Module / Page',
      key: 'name',
      render: (_, row) => (row.type === 'module' ? <strong>{row.module.name}</strong> : row.page.name),
    },
    ...CRUD_CODES.map(code => ({
      title: code.charAt(0) + code.slice(1).toLowerCase(),
      key: code,
      width: 90,
      align: 'center',
      render: (_, row) => {
        if (row.type !== 'page') return null;
        const perm = row.granted.find(p => p.code === code);
        if (perm) return sourceTag(perm);
        if (code === 'VIEW' && row.impliedView) return <Tag>implied</Tag>;
        return <span className="text-muted">—</span>;
      },
    })),
    {
      title: 'Buttons / actions',
      key: 'buttons',
      render: (_, row) =>
        row.type !== 'page'
          ? null
          : row.granted
              .filter(p => p.kind === 'BUTTON')
              .map(p => (
                <Tooltip key={p.id} title={preview.isSuperAdmin ? 'super-admin' : `Granted by: ${(preview.sources?.[p.id] || []).map(id => roleName[id]).join(', ')}`}>
                  <Tag color="blue">{p.name}</Tag>
                </Tooltip>
              )),
    },
  ];

  const inactiveSelected = (preview?.roles || []).filter(r => !r.isActive);

  return (
    <>
      <div className="ac-toolbar">
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Select one or more roles (as a user would hold them in Entra)"
          style={{ minWidth: 420, flex: '1 1 420px' }}
          value={roleIds}
          onChange={setRoleIds}
          optionFilterProp="label"
          options={roles.map(r => ({ value: r.id, label: r.entraRoleValue }))}
        />
      </div>
      {roleIds.length === 0 && <Empty description="Pick roles to see the combined access a user with all of them gets" />}
      {preview?.isSuperAdmin && (
        <Alert className="mb-2" type="warning" showIcon message="A selected role is super-admin: every active application permission is granted. Customer/data scope still applies." />
      )}
      {inactiveSelected.length > 0 && (
        <Alert className="mb-2" type="info" showIcon message={`Inactive roles grant nothing: ${inactiveSelected.map(r => r.entraRoleValue).join(', ')}`} />
      )}
      {preview && (
        <>
          <div className="small text-muted mb-2">
            Combined result is the union of all selected roles: if one role can View and another can Delete, the user can do both.
            External (non-chemplastinc.com) users are additionally limited to the Customer Dashboard.
          </div>
          <Table
            rowKey="key"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={tree}
            pagination={false}
            defaultExpandAllRows
            rowClassName={row => (row.type === 'module' ? 'ac-module-row' : '')}
            scroll={{ x: 900 }}
          />
        </>
      )}
    </>
  );
};

export default PreviewTab;
